/*
 *
 * Copyright (c) Vaisala Oyj. All rights reserved.
 *
 */

import * as React from 'react';
import { RouteObject, useRoutes } from 'react-router-dom';
import HomePage from './pages/HomePage';
import DataManagementPage from './pages/DataManagementPage';
import WindReconstructionPage from './pages/WindReconstructionPage';
import { Layout } from './components/Layout';
import { PageSpinner } from './components/PageSpinner';
import { Application as VaiApplication } from '@vaisala/rockhopper-components';
import { useProject } from './state/project';
import LoginPage from './pages/LoginPage';
import { useAuth } from './state/auth/AuthContext';
import { useEffect } from 'react';
import PrivateRoute from './components/PrivateRoute';

export type AppRoute = RouteObject & {
  name?: string;
  nav?: boolean;
  subpages?: boolean;
  children?: AppRoute[];
};

const MonitoringPage = React.lazy(() => import('./pages/MonitoringPage'));
const ProjectAdminPage = React.lazy(() => import('./pages/ProjectAdminPage'));
const UserManagement = React.lazy(() => import('./pages/UserManagement'));
const Logbook = React.lazy(() => import('./pages/LogbookPage'));
const LidarSNManagementPage = React.lazy(() => import('./pages/LidarSNManagementPage'));


export const initialRoutes: AppRoute[] = [
  {
    path: '/login',
    element: <LoginPage />,
    nav: false,
    name: 'page.login',
  } as AppRoute,
  {
    path: '/',
    element: <Layout />,
    children: [
      {
        index: true,
        element: <HomePage />,
        nav: false,
        name: 'page.home',
      } as AppRoute,
      {
        path: '/logbook/*',
        element: (
          <React.Suspense fallback={<PageSpinner />}>
            <Logbook />
          </React.Suspense>
        ),
        nav: true,
        name: 'Logbook',
      },
      {
        path: '/projects/*',
        element: (
          <React.Suspense fallback={<PageSpinner />}>
            <ProjectAdminPage />
          </React.Suspense>
        ),
        nav: true,
        name: 'page.project_admin',
      },
      {
        path: '/admin',
        nav: true,
        name: 'Admin',
        children: [
          {
            index: true,
            path: '/admin/usersmanagement',
            element: (
              <React.Suspense fallback={<PageSpinner />}>
                <PrivateRoute>
                <UserManagement />
                </PrivateRoute>
              </React.Suspense>
            ),
            nav: true,
            name: 'page.admin.usersmanagement'
          },
          {
            index: true,
            path: '/admin/lidars',
            element: (
              <React.Suspense fallback={<PageSpinner />}>
                <PrivateRoute>
                <LidarSNManagementPage />
                </PrivateRoute>
              </React.Suspense>
            ),
            nav: true,
            name: "page.admin.lidars"
          }
        ]
      },
    ] as AppRoute[],
  },
  {
    path: '*',
    element: '/',
  },
];

const addProjectRoutes = (routes: AppRoute[], projectName: string): AppRoute[] => {
  const newRoutes = [...routes];
  newRoutes[1].children?.push({
    path: '/project/' + projectName,
    nav: true,
    name: projectName,
    children: [
      {
        index: true,
        path: '/project/' + projectName + '/data_management',
        element: <DataManagementPage />,
        nav: true,
        name: 'page.data_management',
      } as AppRoute,
      {
        path: '/project/' + projectName + '/monitoring',
        element: (
          <React.Suspense fallback={<PageSpinner />}>
            <MonitoringPage />
          </React.Suspense>
        ),
        nav: true,
        name: 'page.monitoring',
      } as AppRoute,
      {
        path: '/project/' + projectName + '/wind_reconstruction',
        element: <WindReconstructionPage />,
        nav: true,
        name: 'page.wind_reconstruction',
      },
    ] as AppRoute[],
  });
  return newRoutes;
};

export const Routes = () => {
  const { project, launched } = useProject();
  const [routes, setRoutes] = React.useState<AppRoute[]>(initialRoutes);
  const { checkSession, status, role} = useAuth();

  useEffect(() => {
    if (launched && project.name !== '' && routes[1].children?.length === 4) {
      setRoutes(addProjectRoutes(routes, project.name));
    } else if (!launched && routes[1].children?.length === 5) {
      const updatedRoutes = [...routes];
      updatedRoutes[1].children = updatedRoutes[1].children?.filter((route) => !route.path?.includes('/project/'));
      setRoutes(updatedRoutes);
    }
  }, [launched, project.name]);

  useEffect(() => {
    if (status === null) {
      checkSession();
    }
  }, [status, checkSession]);

  const match = useRoutes(routes);

  return (
    <VaiApplication >
      {status === null && role === null ? (
        <PageSpinner />
      ) : (
        match
      )}
    </VaiApplication>
  );
};
